export function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function normalizeAbacOverviewModule(permissions) {
  if (!isPermissionObject(permissions)) return permissions || {};

  const next = deepClone(permissions);
  const nestedOverview = next.navbar?.overview;

  if (isPermissionObject(nestedOverview)) {
    if (!isPermissionObject(next.overview)) {
      next.overview = nestedOverview;
    }
    if (isPermissionObject(next.navbar)) {
      delete next.navbar.overview;
    }
  }

  return next;
}

export const ABAC_MULTI_ACCESS_HIDDEN_CATALOGUE_LEAVES = [
  "connect_catalogue",
  "create_catalogue",
  "delete_catalogue",
  "disconnect_catalogue",
  "update_catalogue",
];

export const ABAC_NOTIFICATIONS_HIDDEN_MANAGE_LEAVES = [
  "markRead",
  "markAllRead",
  "refresh",
  "mark_read",
  "mark_all_read",
];


export const DU_PLATFORM_DOC_API_KEYS = ["hlr_api", "sms_api"];

export const LEFTOVER_FEATURE_DOC_KEYS = [
  "documentation",
  "messaging",
  "routing",
  "user_templates",
];

export function isLeftoverFeatureDocKey(featureKey) {
  return LEFTOVER_FEATURE_DOC_KEYS.includes(
    String(featureKey || "").trim()
  );
}

const FEATURE_DOCS_USER_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "socials",
  "sms_utilities",
  "reports",
  "address_book",
  "templates",
  "error_code",
];

const FEATURE_DOCS_ADMIN_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "sms_utilities",
  "reports",
  "address_book",
  "templates",
  "approvals",
  "user",
  "company",
  "firewall",
  "sales_teams",
  "error_code",
];

const FEATURE_DOCS_TENANT_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "sms_utilities",
  "reports",
  "address_book",
  "templates",
  "approvals",
  "user",
  "company",
  "vendor",
  "smsc",
  "sales_teams",
];

const FEATURE_DOCS_SUPER_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "documentation",
  "messaging",
  "sms_utilities",
  "reports",
  "address_book",
  "templates",
  "approvals",
  "user",
  "company",
  "vendor",
  "firewall",
  "smsc",
  "dlt",
  "sales_teams",
  "network",
  "error_code",
];

const FEATURE_DOCS_MANAGER_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "reports",
  "user",
  "sales_teams",
];

const FEATURE_DOCS_SELLER_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "reports",
  "user",
];

export const PLATFORM_DOC_FEATURE_KEYS_BY_ROLE = {
  user: FEATURE_DOCS_USER_KEYS,
  admin: FEATURE_DOCS_ADMIN_KEYS,
  tenant: FEATURE_DOCS_TENANT_KEYS,
  superadmin: FEATURE_DOCS_SUPER_KEYS,
  system: FEATURE_DOCS_SUPER_KEYS,
  manager: FEATURE_DOCS_MANAGER_KEYS,
  seller: FEATURE_DOCS_SELLER_KEYS,
};

export function getFeatureDocKeysForRole(role) {
  const roleKey = String(role || "").toLowerCase().trim();

  if (
    Object.prototype.hasOwnProperty.call(
      PLATFORM_DOC_FEATURE_KEYS_BY_ROLE,
      roleKey
    )
  ) {
    return PLATFORM_DOC_FEATURE_KEYS_BY_ROLE[roleKey];
  }

  return PLATFORM_DOC_FEATURE_KEYS_BY_ROLE.admin;
}

export function isUserAbacRole(role) {
  return String(role || "").toLowerCase().trim() === "user";
}

export function resolveFeatureDocKeyForRole(role, featureKey) {
  const key = String(featureKey || "").trim();

  if (key === "messaging" && isUserAbacRole(role)) {
    return "socials";
  }

  return key;
}

export function isFeatureDocKeyAllowedForRole(role, featureKey) {
  const allowed = getFeatureDocKeysForRole(role);

  return (
    allowed == null ||
    allowed.includes(resolveFeatureDocKeyForRole(role, featureKey))
  );
}


export const DU_PLATFORM_DOC_FEATURE_KEYS = [
  "login",
  "common_functionality",
  "dashboard",
  "socials",
  "sms_utilities",
  "reports",
  "address_book",
  "templates",
  "approvals",
  "user",
  "company",
  "vendor",
  "firewall",
  "smsc",
  "dlt",
  "sales_teams",
  "network",
  "error_code",
];

export function normalizePlatformDocumentsShape(tree) {
  if (!isPermissionObject(tree)) return tree;

  const docs = tree.platform_documents;
  if (!isPermissionObject(docs)) return tree;

  const cpaas = isPermissionObject(docs.cpaas_documentation)
    ? { ...docs.cpaas_documentation }
    : {};

  const features = isPermissionObject(cpaas.features_documents)
    ? { ...cpaas.features_documents }
    : {};

  if (isPermissionObject(docs.features_documents)) {
    Object.entries(docs.features_documents).forEach(([key, value]) => {
      if (!Object.prototype.hasOwnProperty.call(features, key)) {
        features[key] = value;
      }
    });
  }

  DU_PLATFORM_DOC_FEATURE_KEYS.forEach((key) => {
    if (
      !Object.prototype.hasOwnProperty.call(features, key) &&
      typeof docs[key] === "boolean"
    ) {
      features[key] = docs[key];
    }
  });

  if (Object.keys(features).length) {
    const nextCpaas = { ...cpaas, features_documents: features };
    // Drop API Documents from the UI tree — not a Manage Permissions card on DU.
    delete nextCpaas.api_documents;
    tree.platform_documents = { cpaas_documentation: nextCpaas };
  }

  return tree;
}

export function filterPlatformDocumentsForDu(tree) {
  if (!isPermissionObject(tree)) return tree;

  normalizePlatformDocumentsShape(tree);

  const docs = tree.platform_documents;
  if (!isPermissionObject(docs)) return tree;

  const features = docs.cpaas_documentation?.features_documents;
  const nextFeatures = {};

  if (isPermissionObject(features)) {
    DU_PLATFORM_DOC_FEATURE_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(features, key)) {
        nextFeatures[key] = features[key];
      }
    });
  }

  const cpaas = {};

  if (Object.keys(nextFeatures).length) {
    cpaas.features_documents = nextFeatures;
  }

  if (Object.keys(cpaas).length) {
    tree.platform_documents = { cpaas_documentation: cpaas };
  } else {
    delete tree.platform_documents;
  }

  return tree;
}

export function filterFeaturesDocumentsForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;
  const allowed = getFeatureDocKeysForRole(role);
  if (allowed == null) return tree;

  const features =
    tree?.platform_documents?.cpaas_documentation?.features_documents;
  if (!isPermissionObject(features)) return tree;

  const allow = new Set(allowed);
  Object.keys(features).forEach((key) => {
    if (!allow.has(key)) delete features[key];
  });
  return tree;
}

export const SMS_DASHBOARD_WIDGETS_BY_ROLE = {
  superadmin: null,
  system: null,
  admin: [
    "account_summary",
    "recent_activity",
    "message_status",
    "operator_country",
    "sender_id",
    "user_distribution",
    "route_percentage",
    "route_distribution",
    "traffic_list",
    "system_dashboard",
  ],
  tenant: [
    "account_summary",
    "recent_activity",
    "message_status",
    "operator_country",
    "sender_id",
    "user_distribution",
    "route_percentage",
    "route_distribution",
    "traffic_list",
    "system_dashboard",
  ],
  user: [
    "account_summary",
    "recent_activity",
    "message_status",
    "operator_country",
    "sender_id",
    "traffic_list",
  ],
};

export function filterSmsDashboardWidgetsForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;

  const roleKey = String(role || "")
    .toLowerCase()
    .trim();

  const allowed = Object.prototype.hasOwnProperty.call(
    SMS_DASHBOARD_WIDGETS_BY_ROLE,
    roleKey
  )
    ? SMS_DASHBOARD_WIDGETS_BY_ROLE[roleKey]
    : SMS_DASHBOARD_WIDGETS_BY_ROLE.admin;

  if (allowed == null) return tree;

  const allow = new Set(allowed);

  const pruneWidgets = (widgets) => {
    if (!isPermissionObject(widgets)) return;

    Object.keys(widgets).forEach((key) => {
      if (!allow.has(key)) delete widgets[key];
    });
  };

  pruneWidgets(tree?.sms?.dashboard?.widgets);
  pruneWidgets(tree?.channels?.sms?.dashboard?.widgets);

  return tree;
}

export const DASHBOARD_OVERVIEW_MERGE_KEYS = [
  "account_summary",
  "recent_activity",
];

export function getDashboardWidgetKeysForRole(role) {
  const roleKey = String(role || "")
    .toLowerCase()
    .trim();
  if (
    Object.prototype.hasOwnProperty.call(SMS_DASHBOARD_WIDGETS_BY_ROLE, roleKey)
  ) {
    return SMS_DASHBOARD_WIDGETS_BY_ROLE[roleKey];
  }
  return SMS_DASHBOARD_WIDGETS_BY_ROLE.admin;
}

function isDashboardWidgetLeafOn(value) {
  if (value === true || value === 1 || value === "true") return true;
  if (value && typeof value === "object") {
    return value.view === true || value.view === 1 || value.view === "true";
  }
  return false;
}

export function mergeOverviewDashboardWidgetsIntoSms(tree) {
  if (!isPermissionObject(tree)) return tree;
  const ovWidgets = tree.overview?.widgets;
  if (!isPermissionObject(ovWidgets)) return tree;

  DASHBOARD_OVERVIEW_MERGE_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(ovWidgets, key)) return;
    if (!isDashboardWidgetLeafOn(ovWidgets[key])) return;
    if (!isPermissionObject(tree.sms)) tree.sms = {};
    if (!isPermissionObject(tree.sms.dashboard)) tree.sms.dashboard = {};
    if (!isPermissionObject(tree.sms.dashboard.widgets)) {
      tree.sms.dashboard.widgets = {};
    }
    const smsWidgets = tree.sms.dashboard.widgets;
    if (!isDashboardWidgetLeafOn(smsWidgets[key])) {
      smsWidgets[key] = true;
    }
  });
  return tree;
}

export function syncMergedDashboardWidgetsToOverview(tree) {
  if (!isPermissionObject(tree)) return tree;
  const smsWidgets =
    tree?.sms?.dashboard?.widgets || tree?.channels?.sms?.dashboard?.widgets;
  if (!isPermissionObject(smsWidgets)) return tree;

  const overview = isPermissionObject(tree.overview) ? { ...tree.overview } : {};
  const ovWidgets = isPermissionObject(overview.widgets)
    ? { ...overview.widgets }
    : {};
  DASHBOARD_OVERVIEW_MERGE_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(smsWidgets, key)) return;
    const on = isDashboardWidgetLeafOn(smsWidgets[key]);
    const existing = ovWidgets[key];
    ovWidgets[key] =
      existing && typeof existing === "object"
        ? { ...existing, view: on }
        : { view: on };
  });
  overview.widgets = ovWidgets;
  tree.overview = overview;
  return tree;
}

export function filterOverviewWidgetsForRole(tree, role) {
  if (!isPermissionObject(tree?.overview)) return tree;
  const roleKey = normalizeRoleKey(role);
  if (roleKey === "superadmin" || roleKey === "system") return tree;
  delete tree.overview;
  return tree;
}

export const SMS_REPORTS_KEYS_BY_ROLE = {
  superadmin: null,
  system: null,
  admin: [
    "campaign",
    "summary",
    "detailed_analytics",
    "delivery_latency",
    "consumption",
    "lookup",
    "aggregator",
    "profit",
    "tps",
    "vendor_summary",
    "edr",
    "coverage",
  ],
  tenant: [
    "campaign",
    "summary",
    "detailed_analytics",
    "delivery_latency",
    "consumption",
    "lookup",
    "aggregator",
    "profit",
    "tps",
    "vendor_summary",
    "edr",
    "coverage",
  ],
  user: [
    "campaign",
    "summary",
    "detailed_analytics",
    "delivery_latency",
    "consumption",
    "lookup",
  ],
};

export function filterSmsReportsForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;

  const roleKey = String(role || "")
    .toLowerCase()
    .trim();

  const allowed = Object.prototype.hasOwnProperty.call(
    SMS_REPORTS_KEYS_BY_ROLE,
    roleKey
  )
    ? SMS_REPORTS_KEYS_BY_ROLE[roleKey]
    : SMS_REPORTS_KEYS_BY_ROLE.admin;

  if (allowed == null) return tree;

  const allow = new Set(allowed);

  const pruneReports = (reports) => {
    if (!isPermissionObject(reports)) return;

    Object.keys(reports).forEach((key) => {
      if (!allow.has(key)) delete reports[key];
    });

    if (
      roleKey === "user" &&
      isPermissionObject(reports.detailed_analytics)
    ) {
      delete reports.detailed_analytics.delivery_smsc;
    }
  };

  pruneReports(tree?.sms?.reports);
  pruneReports(tree?.channels?.sms?.reports);

  return tree;
}

export const VENDOR_ABAC_SCHEMA = {
  view: true,
  list: true,
  create: true,
  update: true,
  delete: true,
  view_smsc: true,
};

export function ensureVendorModuleView(permissions) {
  if (!isPermissionObject(permissions?.vendor)) return permissions;

  const vendor = permissions.vendor;
  if (typeof vendor.view === "boolean") return permissions;

  const hasTruthy = (node) => {
    if (!isPermissionObject(node)) return false;

    return Object.values(node).some((v) => {
      if (v === true || v === "true" || v === 1) return true;
      if (isPermissionObject(v)) return hasTruthy(v);
      return false;
    });
  };

  if (typeof vendor.list === "boolean") {
    const rest = { ...vendor };
    delete rest.list;
    vendor.view = vendor.list || hasTruthy(rest);
  } else {
    vendor.view = hasTruthy(vendor);
  }

  return permissions;
}

export function ensureVendorCatalogueLeaves(tree) {
  if (!isPermissionObject(tree)) return tree;

  if (!isPermissionObject(tree.vendor)) tree.vendor = {};

  ensureVendorModuleView(tree);

  tree.vendor = intersectAbacPermissionsWithSchema(
    VENDOR_ABAC_SCHEMA,
    tree.vendor
  );

  return tree;
}

export function filterVendorModuleForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;

  const roleKey = String(role || "")
    .toLowerCase()
    .trim();

  if (
    roleKey === "superadmin" ||
    roleKey === "system" ||
    roleKey === "tenant"
  ) {
    return tree;
  }

  if (tree.vendor) delete tree.vendor;

  return tree;
}

function deleteKeys(node, keys) {
  if (!isPermissionObject(node)) return;

  keys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(node, key)) {
      delete node[key];
    }
  });
}

function normalizeRoleKey(role) {
  return String(role || "")
    .toLowerCase()
    .trim();
}

function forEachSmsNode(tree, cb) {
  [tree?.sms, tree?.channels?.sms].forEach((sms) => {
    if (isPermissionObject(sms)) cb(sms);
  });
}

export const ROUTING_EDIT_KEYS_BY_ROLE = {
  admin: ["BasicView", "export", "BasicUpdate", "delete"],
  tenant: [
    "BasicView",
    "OptionalView",
    "MMsView",
    "export",
    "apply",
    "BasicUpdate",
    "OptionalUpdate",
    "MmsUpdate",
    "delete",
    "undo",
    "previous",
  ],
};

export const ROUTING_ADD_KEYS_BY_ROLE = {
  tenant: ["view", "import", "apply", "create"],
};

export function filterSmsRoutingForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;
  const roleKey = normalizeRoleKey(role);
  if (roleKey === "superadmin" || roleKey === "system") return tree;

  forEachSmsNode(tree, (sms) => {
    if (roleKey === "user") {
      delete sms.routing;
      return;
    }
    if (!isPermissionObject(sms.routing)) return;

    delete sms.routing.sender;

    if (roleKey !== "tenant") {
      delete sms.routing.add;
    } else if (isPermissionObject(sms.routing.add)) {
      const allowedAdd = new Set(ROUTING_ADD_KEYS_BY_ROLE.tenant);
      Object.keys(sms.routing.add).forEach((key) => {
        if (!allowedAdd.has(key)) delete sms.routing.add[key];
      });
    }

    if (isPermissionObject(sms.routing.edit)) {
      const allowed =
        ROUTING_EDIT_KEYS_BY_ROLE[roleKey] || ROUTING_EDIT_KEYS_BY_ROLE.admin;
      const allow = new Set(allowed);
      Object.keys(sms.routing.edit).forEach((key) => {
        if (!allow.has(key)) delete sms.routing.edit[key];
      });
    }
  });

  return tree;
}

export function filterProductModulesForRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;

  const roleKey = normalizeRoleKey(role);

  if (roleKey === "superadmin" || roleKey === "system") return tree;

  const sms = isPermissionObject(tree.sms) ? tree.sms : null;

  if (roleKey === "admin") {
    deleteKeys(tree, ["vendor", "smsc", "dlt", "network"]);

    if (sms) {
      deleteKeys(sms, ["smsc_providers"]);

      if (isPermissionObject(sms.sms_utilities)) {
        delete sms.sms_utilities.pcap;
      }
    }

    if (isPermissionObject(tree.user)) {
      delete tree.user.limit;
      delete tree.user.transaction_mode;
    }

    if (isPermissionObject(tree.error_code)) {
      delete tree.error_code.smsc;
    }

    return tree;
  }

  if (roleKey === "tenant") {
    deleteKeys(tree, ["dlt", "network"]);

    if (sms) {
      if (isPermissionObject(sms.sms_utilities)) {
        delete sms.sms_utilities.pcap;
      }

      if (isPermissionObject(sms.smsc_providers)) {
        deleteKeys(sms.smsc_providers, [
          "http_smsc",
          "alert",
          "limits",
          "hlr",
        ]);
      }
    }

    if (isPermissionObject(tree.smsc)) {
      deleteKeys(tree.smsc, ["http", "alert", "limits", "hlr"]);
    }

    if (isPermissionObject(tree.user)) {
      delete tree.user.limit;
    }

    return tree;
  }

  if (roleKey === "user") {
    deleteKeys(tree, [
      "user",
      "sales_team",
      "company",
      "vendor",
      "smsc",
      "dlt",
      "network",
    ]);

    if (isPermissionObject(tree.navbar)) {
      delete tree.navbar.customize_panel;
    }

    if (sms) {
      deleteKeys(sms, ["routing", "firewall", "smsc_providers"]);

      if (isPermissionObject(sms.sms_utilities)) {
        delete sms.sms_utilities.approvals;
        delete sms.sms_utilities.pcap;
      }
    }

    if (isPermissionObject(tree.error_code)) {
      delete tree.error_code.smsc;
    }

    return tree;
  }

  return tree;
}

export function stripProductUnsupportedAbacModules(tree) {
  if (!isPermissionObject(tree)) return tree;

  deleteKeys(tree, ["billing", "error_code"]);

  return tree;
}

export function filterSmsOrphanLeavesForDu(tree) {
  if (!isPermissionObject(tree)) return tree;

  forEachSmsNode(tree, (sms) => {
    if (isPermissionObject(sms.message?.messages)) {
      delete sms.message.messages.download;
    }
    if (isPermissionObject(sms.template)) {
      delete sms.template.download;
      delete sms.template.publish;
    }
    if (isPermissionObject(sms.sms_utilities?.campaign_manager)) {
      delete sms.sms_utilities.campaign_manager.download;
    }
    if (isPermissionObject(sms.sms_utilities)) {
      delete sms.sms_utilities.character_converter;
    }
    if (isPermissionObject(sms.routing?.copy)) {
      delete sms.routing.copy.download;
    }
    if (isPermissionObject(sms.reports?.detailed_analytics?.delivery_smsc)) {
      delete sms.reports.detailed_analytics.delivery_smsc.download;
    }
    if (isPermissionObject(sms.smsc_providers?.error_code)) {
      delete sms.smsc_providers.error_code.create;
    }
    // Sender Approve/Reject has no delete in backend JSON — always available in UI.
    if (isPermissionObject(sms.sms_utilities?.approvals?.sender?.approve_reject)) {
      delete sms.sms_utilities.approvals.sender.approve_reject.delete;
    }
  });

  // ConfigurationUser is view-only for every role (tenant / admin / user) — no Update toggle.
  if (isPermissionObject(tree.user?.configuration)) {
    const viewOn =
      tree.user.configuration.view === true ||
      tree.user.configuration.view === "true" ||
      tree.user.configuration.view === 1;
    tree.user.configuration = { view: viewOn };
  }

  return tree;
}

export function filterAbacTreeForParentRole(tree, role) {
  if (!isPermissionObject(tree)) return tree;

  stripProductUnsupportedAbacModules(tree);
  filterPlatformDocumentsForDu(tree);
  filterFeaturesDocumentsForRole(tree, role);
  filterSmsOrphanLeavesForDu(tree);
  filterSmsDashboardWidgetsForRole(tree, role);
  filterOverviewWidgetsForRole(tree, role);
  filterSmsReportsForRole(tree, role);
  filterVendorModuleForRole(tree, role);
  filterProductModulesForRole(tree, role);
  filterSmsRoutingForRole(tree, role);

  return tree;
}

export function stripMultiAccessHiddenAbacLeaves(permissions) {
  if (!isPermissionObject(permissions)) {
    return { visible: permissions || {}, preserved: {} };
  }

  const visible = deepClone(permissions);
  const preserved = {};

  [
    ["whatsapp", "catalogue"],
    ["channels", "whatsapp", "catalogue"],
  ].forEach((path) => {
    const catalogue = getValueAtPath(visible, path);
    if (!isPermissionObject(catalogue)) return;

    const kept = {};

    ABAC_MULTI_ACCESS_HIDDEN_CATALOGUE_LEAVES.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(catalogue, key)) {
        kept[key] = catalogue[key];
        delete catalogue[key];
      }
    });

    if (Object.keys(kept).length) {
      preserved[path.join(".")] = kept;
    }
  });

  const notifications = visible?.navbar?.notifications;

  if (isPermissionObject(notifications)) {
    ABAC_NOTIFICATIONS_HIDDEN_MANAGE_LEAVES.forEach((key) => {
      delete notifications[key];
    });
  }

  if (isPermissionObject(visible?.navbar)) {
    delete visible.navbar.navigation;
  }

  const navbarProfile = visible?.navbar?.profile;
  if (isPermissionObject(navbarProfile)) {
    ["regenerate_api_key", "regenerateApiKey"].forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(navbarProfile, key)) return;
      preserved["navbar.profile"] = {
        ...(preserved["navbar.profile"] || {}),
        [key]: navbarProfile[key],
      };
      delete navbarProfile[key];
    });
  }

  const userToken = visible?.user?.token;
  if (isPermissionObject(userToken) && Object.prototype.hasOwnProperty.call(userToken, "regenerate")) {
    preserved["user.token"] = {
      ...(preserved["user.token"] || {}),
      regenerate: userToken.regenerate,
    };
    delete userToken.regenerate;
  }

  if (isPermissionObject(visible?.sales_team)) {
    delete visible.sales_team.list;
  }

  [
    ["email", "dashboard", "widgets"],
    ["channels", "email", "dashboard", "widgets"],
  ].forEach((path) => {
    const widgets = getValueAtPath(visible, path);
    if (!isPermissionObject(widgets)) return;

    if (
      !Object.prototype.hasOwnProperty.call(
        widgets,
        "schedule_status_distribution"
      )
    ) {
      return;
    }

    const pathKey = path.join(".");

    preserved[pathKey] = {
      ...(preserved[pathKey] || {}),
      schedule_status_distribution: widgets.schedule_status_distribution,
    };

    delete widgets.schedule_status_distribution;
  });

  [
    ["email", "frame"],
    ["channels", "email", "frame"],
  ].forEach((path) => {
    const frame = getValueAtPath(visible, path);
    if (!isPermissionObject(frame)) return;

    if (!Object.prototype.hasOwnProperty.call(frame, "download")) return;

    const pathKey = path.join(".");

    preserved[pathKey] = {
      ...(preserved[pathKey] || {}),
      download: frame.download,
    };

    delete frame.download;
  });

  return { visible, preserved };
}

export function stripNotificationsHiddenLeavesForSave(permissions) {
  if (!isPermissionObject(permissions)) return permissions || {};

  const next = deepClone(permissions);
  const notifications = next?.navbar?.notifications;

  if (isPermissionObject(notifications)) {
    ABAC_NOTIFICATIONS_HIDDEN_MANAGE_LEAVES.forEach((key) => {
      delete notifications[key];
    });
  }

  if (isPermissionObject(next?.navbar)) {
    delete next.navbar.navigation;
  }

  if (isPermissionObject(next?.sales_team)) {
    delete next.sales_team.list;
  }

  return next;
}

export function resolvePreservedHiddenAbacLeaves(rawPreserved, uiPreserved) {
  const raw = isPermissionObject(rawPreserved) ? rawPreserved : {};
  const ui = isPermissionObject(uiPreserved) ? uiPreserved : {};

  if (!Object.keys(ui).length) return raw;

  const rawLeaves = Object.assign({}, ...Object.values(raw));
  const out = {};

  Object.entries(ui).forEach(([pathKey, leaves]) => {
    out[pathKey] = { ...leaves, ...rawLeaves };
  });

  return out;
}

export function mergePreservedAbacLeaves(permissions, preserved) {
  if (!isPermissionObject(permissions) || !isPermissionObject(preserved)) {
    return permissions || {};
  }

  const next = deepClone(permissions);

  Object.entries(preserved).forEach(([pathKey, leaves]) => {
    if (!isPermissionObject(leaves)) return;

    const segments = String(pathKey)
      .split(".")
      .map((s) => s.trim())
      .filter(Boolean);

    if (!segments.length) return;

    let cursor = next;

    for (let i = 0; i < segments.length - 1; i += 1) {
      const key = segments[i];

      if (!isPermissionObject(cursor[key])) {
        cursor[key] = {};
      }

      cursor = cursor[key];
    }

    const leafParentKey = segments[segments.length - 1];

    if (!isPermissionObject(cursor[leafParentKey])) {
      cursor[leafParentKey] = {};
    }

    Object.assign(cursor[leafParentKey], leaves);
  });

  return next;
}

export const CPAAS_BUSINESS_API_ORDER = [
  "telegram_business_api",
  "messenger_business_api",
  "sms_business_api",
  "email_business_api",
  "instagram_business_api",
  "rcs_business_api",
  "whatsapp_business_api",
  "push_client_business_api",
];

function coercePermissionLeafToBoolean(raw) {
  if (typeof raw === "boolean") return raw;
  if (!isPermissionObject(raw)) return false;

  if (typeof raw.view === "boolean") return raw.view;

  return Object.values(raw).some(
    (v) => v === true || v === "true" || v === 1
  );
}

function orderedCpaasBusinessApiKeys(business) {
  const known = CPAAS_BUSINESS_API_ORDER.filter((key) =>
    Object.prototype.hasOwnProperty.call(business, key)
  );

  const extras = Object.keys(business).filter(
    (key) => !CPAAS_BUSINESS_API_ORDER.includes(key)
  );

  return [...known, ...extras];
}

export function normalizeCpaasBusinessApiForUi(permissions) {
  if (!isPermissionObject(permissions)) return permissions || {};

  const business = permissions?.platform_documents?.cpaas_business_api;
  if (!isPermissionObject(business)) return permissions;

  const next = deepClone(permissions);
  const out = {};

  orderedCpaasBusinessApiKeys(business).forEach((key) => {
    out[key] = coercePermissionLeafToBoolean(business[key]);
  });

  next.platform_documents = {
    ...next.platform_documents,
    cpaas_business_api: out,
  };

  return next;
}

export function expandCpaasBusinessApiForSave(permissions) {
  if (!isPermissionObject(permissions)) return permissions || {};

  const business = permissions?.platform_documents?.cpaas_business_api;
  if (!isPermissionObject(business)) return permissions;

  const next = deepClone(permissions);
  const out = {};

  orderedCpaasBusinessApiKeys(business).forEach((key) => {
    const enabled = coercePermissionLeafToBoolean(business[key]);
    out[key] = { view: enabled, download: false };
  });

  next.platform_documents = {
    ...next.platform_documents,
    cpaas_business_api: out,
  };

  return next;
}

export function intersectAbacPermissionsWithSchema(schema, values) {
  if (!isPermissionObject(schema)) return {};

  const src = isPermissionObject(values) ? values : {};
  const out = {};

  Object.entries(schema).forEach(([key, schemaVal]) => {
    if (key === "role" || String(key).startsWith("_")) {
      if (Object.prototype.hasOwnProperty.call(src, key)) {
        out[key] = src[key];
      }
      return;
    }

    if (typeof schemaVal === "boolean") {
      out[key] = coercePermissionLeafToBoolean(src[key]);
      return;
    }

    if (isPermissionObject(schemaVal)) {
      out[key] = intersectAbacPermissionsWithSchema(
        schemaVal,
        isPermissionObject(src[key]) ? src[key] : {}
      );
    }
  });

  return out;
}

export function extractAbacPermissionsOutsideSchema(schema, values) {
  if (!isPermissionObject(values)) return {};
  if (!isPermissionObject(schema)) return deepClone(values);

  const extras = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === "role" || String(key).startsWith("_")) return;

    if (!Object.prototype.hasOwnProperty.call(schema, key)) {
      extras[key] = deepClone(value);
      return;
    }

    if (isPermissionObject(schema[key]) && isPermissionObject(value)) {
      const childExtras = extractAbacPermissionsOutsideSchema(
        schema[key],
        value
      );

      if (Object.keys(childExtras).length) {
        extras[key] = childExtras;
      }
    }
  });

  return extras;
}

export function mergeAbacPermissionTrees(base, overlay) {
  if (!isPermissionObject(overlay)) return base || {};
  if (!isPermissionObject(base)) return deepClone(overlay);

  const next = deepClone(base);

  Object.entries(overlay).forEach(([key, value]) => {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = deepClone(value);
      return;
    }

    if (isPermissionObject(next[key]) && isPermissionObject(value)) {
      next[key] = mergeAbacPermissionTrees(next[key], value);
    }
  });

  return next;
}

export const ABAC_TOP_MODULE_ORDER = [
  "default",
  "navbar",
  "sms",
  "user",
  "sales_team",
  "company",
  "vendor",
  "address_book",
  "smsc",
  "dlt",
  "network",
  "platform_documents",
];

export function sortAbacTopModules(moduleKeys) {
  const keys = Array.isArray(moduleKeys) ? [...moduleKeys] : [];
  const rank = new Map(ABAC_TOP_MODULE_ORDER.map((k, i) => [k, i]));

  return keys.sort((a, b) => {
    const ra = rank.has(a) ? rank.get(a) : 1000;
    const rb = rank.has(b) ? rank.get(b) : 1000;

    if (ra !== rb) return ra - rb;

    return String(a).localeCompare(String(b));
  });
}

export function formatPermissionLabel(key) {
  if (!key) return "";

  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function isPermissionObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function collectLeafPaths(node, basePath = []) {
  if (typeof node === "boolean") {
    return [basePath];
  }

  if (!isPermissionObject(node)) {
    return [];
  }

  return Object.entries(node).flatMap(([key, value]) =>
    collectLeafPaths(value, [...basePath, key])
  );
}

export function getValueAtPath(node, path) {
  return path.reduce((current, key) => current?.[key], node);
}

export function setValueAtPath(node, path, value) {
  const cloned = deepClone(node);
  let cursor = cloned;

  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];

    if (!isPermissionObject(cursor[key])) {
      cursor[key] = {};
    }

    cursor = cursor[key];
  }

  cursor[path[path.length - 1]] = value;

  return cloned;
}

export function setLeafPermission(node, path, value) {
  if (!path?.length) return node;

  return setValueAtPath(node, path, value);
}

export function setLeafWithViewCascade(node, path, value) {
  return setLeafPermission(node, path, value);
}

export function splitPermissionEntries(node) {
  if (!isPermissionObject(node)) {
    return { booleanEntries: [], nestedEntries: [] };
  }

  const booleanEntries = [];
  const nestedEntries = [];

  Object.entries(node).forEach(([key, value]) => {
    if (typeof value === "boolean") {
      booleanEntries.push([key, value]);
    } else if (isPermissionObject(value)) {
      nestedEntries.push([key, value]);
    }
  });

  booleanEntries.sort(([a], [b]) => {
    if (a === "view") return -1;
    if (b === "view") return 1;

    return a.localeCompare(b);
  });

  return { booleanEntries, nestedEntries };
}

export function getBranchCheckState(node) {
  const paths = collectLeafPaths(node);

  if (paths.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const values = paths.map((path) => Boolean(getValueAtPath(node, path)));
  const allTrue = values.every(Boolean);
  const allFalse = values.every((val) => !val);

  return {
    checked: allTrue,
    indeterminate: !allTrue && !allFalse,
  };
}

export function setBranchValue(node, pathPrefix, value) {
  const branch = getValueAtPath(node, pathPrefix);
  const leafPaths = collectLeafPaths(branch, pathPrefix);

  return leafPaths.reduce(
    (acc, path) => setValueAtPath(acc, path, value),
    node
  );
}

export function filterPermissionTree(node, searchTerm, parentLabels = []) {
  const query = searchTerm.trim().toLowerCase();

  if (!query) {
    return node;
  }

  if (typeof node === "boolean") {
    const labelPath = parentLabels.join(" ").toLowerCase();
    return labelPath.includes(query) ? node : null;
  }

  if (!isPermissionObject(node)) {
    return null;
  }

  const filtered = {};

  Object.entries(node).forEach(([key, value]) => {
    const labels = [...parentLabels, formatPermissionLabel(key)];
    const labelPath = labels.join(" ").toLowerCase();
    const keyMatches = key.toLowerCase().includes(query);
    const child = filterPermissionTree(value, searchTerm, labels);

    if (keyMatches) {
      filtered[key] = value;
    } else if (child !== null && child !== undefined) {
      filtered[key] = child;
    } else if (labelPath.includes(query) && typeof value === "boolean") {
      filtered[key] = value;
    }
  });

  return Object.keys(filtered).length > 0 ? filtered : null;
}

export function countEnabledPermissions(node) {
  if (typeof node === "boolean") {
    return node ? 1 : 0;
  }

  if (!isPermissionObject(node)) {
    return 0;
  }

  return Object.values(node).reduce(
    (total, value) => total + countEnabledPermissions(value),
    0
  );
}

export function countTotalPermissions(node) {
  if (typeof node === "boolean") {
    return 1;
  }

  if (!isPermissionObject(node)) {
    return 0;
  }

  return Object.values(node).reduce(
    (total, value) => total + countTotalPermissions(value),
    0
  );
}
